// client/src/pages/AttorneyDashboard.tsx - UPDATED TO USE EXPERT_DIRECTORY
import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import StateSelect from '../components/StateSelect';
import MessageModal from '../components/MessageModal';
import SubscriptionModal from '../components/SubscriptionModal';
import { useNavigate } from 'react-router-dom';
import CasePosts from '../components/CasePosts';

interface ExpertMatch {
    id: string;
    specialty: string;
    sub_specialty?: string;
    years_practicing?: number;
    retainer_fee?: number;
    rate_review?: number;
    rate_dep?: number;
    rate_trial?: number;
    states_licensed?: string[];
    currently_practicing?: boolean;
    published_scholarly?: boolean;
    is_verified: boolean;
    match_confidence?: number;
    match_reasons?: string;
    num_defense_depositions?: number;
    num_plaintiff_depositions?: number;
    num_defense_trials?: number;
    num_plaintiff_trials?: number;
    tier?: number;
    profile_picture_url?: string;
    full_name?: string;
    phone_number?: string;
    email?: string;
    job_title?: string;
    expert_type?: string;
    certifications?: string[];
    case_experience?: string;
    contact_status?: string;
}

const MEDICAL_SPECIALTIES = [
    { value: 'anesthesiology', label: 'Anesthesiology', subSpecialties: ['Pain Management', 'Critical Care Anesthesiology', 'Pediatric Anesthesiology', 'Obstetric Anesthesiology'] },
    { value: 'cardiology', label: 'Cardiology', subSpecialties: ['Interventional Cardiology', 'Electrophysiology', 'Heart Failure', 'Pediatric Cardiology'] },
    { value: 'dermatology', label: 'Dermatology', subSpecialties: ['Mohs Surgery', 'Pediatric Dermatology', 'Cosmetic Dermatology', 'Immunodermatology'] },
    { value: 'emergency_medicine', label: 'Emergency Medicine', subSpecialties: ['Pediatric Emergency Medicine', 'Sports Medicine', 'Medical Toxicology', 'Wilderness Medicine'] },
    { value: 'endocrinology', label: 'Endocrinology', subSpecialties: ['Diabetes', 'Thyroid Disorders', 'Metabolic Bone Disease', 'Reproductive Endocrinology'] },
    { value: 'family_medicine', label: 'Family Medicine', subSpecialties: ['Sports Medicine', 'Geriatric Medicine', 'Adolescent Medicine', 'Hospice and Palliative Medicine'] },
    { value: 'gastroenterology', label: 'Gastroenterology', subSpecialties: ['Hepatology', 'Inflammatory Bowel Disease', 'Pancreatology', 'Motility Disorders'] },
    { value: 'internal_medicine', label: 'Internal Medicine', subSpecialties: ['Hospital Medicine', 'Geriatric Medicine', 'Sports Medicine', 'Clinical Informatics'] },
    { value: 'neurology', label: 'Neurology', subSpecialties: ['Stroke Neurology', 'Epilepsy', 'Movement Disorders', 'Neuromuscular Medicine'] },
    { value: 'nursing', label: 'Nursing', subSpecialties: ['Critical Care Nursing', 'Emergency Room Nursing', 'Perioperative/OR Nursing', 'Medical-Surgical Nursing', 'Pediatric Nursing', 'Neonatal/NICU Nursing', 'Obstetric/Labor & Delivery', 'Psychiatric/Mental Health Nursing', 'Geriatric Nursing', 'Oncology Nursing', 'Cardiac/Telemetry Nursing', 'Rehabilitation Nursing', 'Home Health Nursing', 'Infection Control', 'Wound Care Specialist', 'Pain Management Nursing', 'Hospice/Palliative Care', 'Case Management', 'Legal Nurse Consultant', 'Forensic Nursing', 'Nurse Practitioner - Family', 'Nurse Practitioner - Adult', 'Nurse Practitioner - Pediatric', 'Nurse Practitioner - Psychiatric', 'Clinical Nurse Specialist', 'Certified Nurse Midwife', 'Certified Registered Nurse Anesthetist'] },
    { value: 'obstetrics_gynecology', label: 'Obstetrics & Gynecology', subSpecialties: ['Maternal-Fetal Medicine', 'Reproductive Endocrinology', 'Gynecologic Oncology', 'Urogynecology'] },
    { value: 'oncology', label: 'Oncology', subSpecialties: ['Medical Oncology', 'Radiation Oncology', 'Pediatric Oncology', 'Surgical Oncology'] },
    { value: 'orthopedics', label: 'Orthopedics', subSpecialties: ['Spine Surgery', 'Joint Replacement', 'Sports Medicine', 'Pediatric Orthopedics'] },
    { value: 'pediatrics', label: 'Pediatrics', subSpecialties: ['Pediatric Neurology', 'Pediatric Cardiology', 'Neonatology', 'Pediatric Emergency Medicine'] },
    { value: 'psychiatry', label: 'Psychiatry', subSpecialties: ['Forensic Psychiatry', 'Child Psychiatry', 'Addiction Psychiatry', 'Geriatric Psychiatry'] },
    { value: 'radiology', label: 'Radiology', subSpecialties: ['Neuroradiology', 'Interventional Radiology', 'Pediatric Radiology', 'Nuclear Radiology'] },
    { value: 'surgery', label: 'Surgery', subSpecialties: ['Trauma Surgery', 'Vascular Surgery', 'Plastic Surgery', 'Pediatric Surgery'] },
    { value: 'therapy', label: 'Therapy', subSpecialties: ['Physical Therapy', 'Occupational Therapy', 'Speech Therapy', 'Respiratory Therapy', 'Massage Therapy', 'Behavioral Therapy'] },
    { value: 'urology', label: 'Urology', subSpecialties: ['Pediatric Urology', 'Urologic Oncology', 'Female Urology', 'Male Infertility'] },
];



const TIER_COLORS = {
    1: { border: 'border-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-800', name: 'Elite', description: 'No challenges or survived all. Stellar vetting, excellent reviews. Ivy League, publications, academic affiliations.' },
    2: { border: 'border-purple-500', bg: 'bg-purple-50', text: 'text-purple-800', name: 'Verified Pro', description: 'Strong court experience. Excellent reviews, reliable delivery. Teaching/speaking engagements.' },
    3: { border: 'border-blue-500', bg: 'bg-blue-50', text: 'text-blue-800', name: 'Experienced', description: 'Consistent case work. Solid reviews, growing courtroom record. Strengthening admissibility.' },
    4: { border: 'border-green-500', bg: 'bg-green-50', text: 'text-green-800', name: 'Emerging', description: 'Board certified. Early litigation activity. Rate transparency. Building review profile.' },
    5: { border: 'border-gray-500', bg: 'bg-gray-50', text: 'text-gray-800', name: 'Listed', description: 'Verified identity. No sanctions. Limited litigation history. Building experience.' }
};

export default function AttorneyDashboard() {
    const navigate = useNavigate();
    const [specialty, setSpecialty] = useState('');
    const [subSpecialty, setSubSpecialty] = useState('');
    const [minYears, setMinYears] = useState<number>(0);
    const [maxRetainer, setMaxRetainer] = useState<number>(0);
    const [statesLicensed, setStatesLicensed] = useState<string[]>([]);
    const [searchKeywords, setSearchKeywords] = useState('');
    const [suggestedKeywords, setSuggestedKeywords] = useState<string[]>([]);
    const [matches, setMatches] = useState<ExpertMatch[]>([]);
    const [attorney, setAttorney] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [selectedExpert, setSelectedExpert] = useState<ExpertMatch | null>(null);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [checkingSubscription, setCheckingSubscription] = useState(true);
    const [messages, setMessages] = useState<any[]>([]);
    const [selectedConversation, setSelectedConversation] = useState<any>(null);
    const [casePostDetails, setCasePostDetails] = useState<any>(null);
    const [replyMessage, setReplyMessage] = useState('');
    const [replyAttachment, setReplyAttachment] = useState<File | null>(null);
    const [sendingReply, setSendingReply] = useState(false);
    const [anonymousAttorneyReply, setAnonymousAttorneyReply] = useState(false);
    const [activeTab, setActiveTab] = useState<'search' | 'messages' | 'case-posts'>('search');
    const [expertResponses, setExpertResponses] = useState<Set<string>>(new Set());

    const selectedSpecialty = MEDICAL_SPECIALTIES.find(s => s.value === specialty);
    const availableSubSpecialties = selectedSpecialty?.subSpecialties || [];

    const checkSubscription = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data: subscription } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', user.id)
                .eq('status', 'active')
                .maybeSingle();
            setHasSubscription(!!subscription);
        } catch (error) {
            console.error('Error checking subscription:', error);
        } finally {
            setCheckingSubscription(false);
        }
    };

    useEffect(() => {
        loadDashboard();
        checkSubscription();

        let reloadTimeout: any = null;
        const channel = supabase
            .channel('expert-messages')
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'messages',
            }, () => {
                if (reloadTimeout) clearTimeout(reloadTimeout);
                reloadTimeout = setTimeout(() => loadDashboard(), 2000);
            })
            .subscribe();

        // Polling fallback in case realtime isn't enabled
        const pollInterval = setInterval(() => {
            if (attorney) loadAttorneyMessages(attorney.id);
        }, 30000);

        return () => {
            supabase.removeChannel(channel);
            clearInterval(pollInterval);
        };
    }, []);

    // Keep selectedConversation in sync when messages update
    useEffect(() => {
        if (!selectedConversation || messages.length === 0) return;

        const convKey = selectedConversation.key;
        // Rebuild to find updated version
        const updatedConvs: any = {};
        messages.forEach((msg: any) => {
            const otherUserId = msg.sender_type === 'attorney' ? msg.recipient_id : msg.sender_id;
            const key = `${otherUserId}-${(msg.case_title || 'untitled').toLowerCase().trim()}`;
            if (!updatedConvs[key]) {
                updatedConvs[key] = {
                    key,
                    expert_id: msg.expert_id,
                    case_title: msg.case_title,
                    messages: [],
                    latest: msg.created_at,
                    unread: 0,
                    urgency: msg.urgency,
                    case_type: msg.case_type,
                    hospital_provider: msg.hospital_provider,
                    incident_date: msg.incident_date,
                    expert: msg.experts || null || selectedConversation.expert
                };
            }
            if (msg.experts && !updatedConvs[key].expert?.full_name) {
                updatedConvs[key].expert = msg.experts;
            }
            if (msg.expert_id && !updatedConvs[key].expert_id) {
                updatedConvs[key].expert_id = msg.expert_id;
            }
            updatedConvs[key].messages.push(msg);
            if (new Date(msg.created_at) > new Date(updatedConvs[key].latest)) {
                updatedConvs[key].latest = msg.created_at;
            }
            if (!msg.read && msg.sender_type === 'expert') {
                updatedConvs[key].unread++;
            }
        });

        if (updatedConvs[convKey]) {
            updatedConvs[convKey].messages.sort((a: any, b: any) =>
                new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );
            setSelectedConversation(updatedConvs[convKey]);
        }
    }, [messages]);

    const handleSpecialtyChange = async (value: string) => {
        setSpecialty(value);
        setSubSpecialty('');
        setSuggestedKeywords([]);

        if (!value) return;

        // Fetch real admin-assigned keywords from verified experts in this specialty
        const specLabel = MEDICAL_SPECIALTIES.find(s => s.value === value)?.label || value;
        const { data } = await supabase
            .from('expert_directory')
            .select('extracted_keywords')
            .ilike('specialty', `%${specLabel}%`);

        if (data) {
            // Collect all keywords, split by commas, deduplicate
            const allKeywords = data
                .map(e => e.extracted_keywords || '')
                .filter(k => k.trim())
                .flatMap(k => k.split(',').map((s: string) => s.trim()))
                .filter(k => k.length > 0);
            const unique = Array.from(new Set(allKeywords));
            setSuggestedKeywords(unique);
        }
    };

    const loadDashboard = async () => {
        try {
            setDashboardLoading(true);
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                navigate('/login');
                return;
            }
            const { data: attorneyData, error: attorneyError } = await supabase
                .from('attorneys')
                .select('*')
                .eq('user_id', user.id)
                .maybeSingle();
            if (attorneyError && attorneyError.code === 'PGRST116') {
                navigate('/attorney-intake');
                return;
            }
            if (attorneyError) {
                setMessage(`Error loading attorney profile: ${attorneyError.message}`);
                return;
            }
            if (attorneyData) {
                setAttorney(attorneyData);
                await loadAttorneyMessages(attorneyData.id);

                // Update last_active_at
                await supabase.from('attorneys').update({ last_active_at: new Date().toISOString() }).eq('id', attorneyData.id);
            }
        } catch (error: any) {
            setMessage(`Error loading dashboard: ${error.message}`);
        } finally {
            setDashboardLoading(false);
        }
    };

    const loadAttorneyMessages = async (attorneyId: string) => {
        try {
            const { data, error } = await supabase
                .from('messages')
                .select(`
                    *,
                    experts (
                        id,
                        full_name,
                        specialty,
                        sub_specialty,
                        is_verified,
                        email,
                        phone_number,
                        tier,
                        profile_picture_url,
                        fee_schedule_url,
                        cv_url
                    )
                `)
                .eq('attorney_id', attorneyId)
                .order('created_at', { ascending: false });
            if (error) {
                console.error('Error loading attorney messages:', error);
            } else {
                // Strip expert identity from anonymous messages before setting state
                const sanitized = (data || []).map(msg => {
                    if (msg.is_anonymous && msg.sender_type === 'expert' && msg.experts) {
                        return {
                            ...msg,
                            experts: {
                                ...msg.experts,
                                full_name: 'Anonymous Expert',
                                email: null,
                                phone_number: null,
                            }
                        };
                    }
                    return msg;
                });
                setMessages(sanitized);
                const respondedExperts = new Set<string>();
                (data || []).forEach(msg => {
                    if (msg.sender_type === 'expert' && msg.expert_id) {
                        respondedExperts.add(msg.expert_id);
                    }
                });
                setExpertResponses(respondedExperts);
                const unreadIds = (data || []).filter(m => !m.read && m.sender_type === 'expert').map(m => m.id);
                if (unreadIds.length > 0) {
                    await supabase.from('messages').update({ read: true }).in('id', unreadIds);
                }
            }
        } catch (error: any) {
            console.error('Error loading messages:', error);
        }
    };

    const sendReply = async () => {
        if (!replyMessage.trim() || !selectedConversation || !attorney) return;
        setSendingReply(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            // Get the expert's user_id from the conversation messages
            // This works even for anonymous experts since sender_id is always present
            const expertMessage = selectedConversation.messages.find((m: any) => m.sender_type === 'expert');
            let recipientUserId = expertMessage?.sender_id;
            // Find expert_id from ANY message in the conversation that has it
            let expertId = selectedConversation.expert_id
                || expertMessage?.expert_id
                || selectedConversation.messages.find((m: any) => m.expert_id)?.expert_id
                || null;

            // If still no recipient, try from the recipient_id of attorney's own messages
            if (!recipientUserId) {
                const attorneyMsg = selectedConversation.messages.find((m: any) => m.sender_type === 'attorney');
                recipientUserId = attorneyMsg?.recipient_id;
            }

            // If we have a recipient but no expert_id, look it up from the experts table
            if (!expertId && recipientUserId) {
                const { data: expertRow } = await supabase
                    .from('experts')
                    .select('id')
                    .eq('user_id', recipientUserId)
                    .single();
                if (expertRow) {
                    expertId = expertRow.id;
                }
            }

            if (!recipientUserId) {
                throw new Error('Could not determine message recipient');
            }

            const messageData = {
                attorney_id: attorney.id,
                expert_id: expertId,
                sender_type: 'attorney' as const,
                message: replyMessage.trim(),
                case_title: selectedConversation.case_title,
                hospital_provider: selectedConversation.hospital_provider || null,
                incident_date: selectedConversation.incident_date || null,
                case_type: selectedConversation.case_type || 'not_specified',
                urgency: selectedConversation.urgency || 'standard',
                attachment_url: null as string | null,
                sender_id: user.id,
                recipient_id: recipientUserId,
                read: false,
                is_anonymous: anonymousAttorneyReply,
                created_at: new Date().toISOString()
            };

            // Upload attachment if provided
            let attachmentUrl = null;
            if (replyAttachment) {
                try {
                    const fileExt = replyAttachment.name.split('.').pop();
                    const fileName = `${user.id}-${Date.now()}.${fileExt}`;
                    const { error: uploadError } = await supabase.storage
                        .from('case-documents')
                        .upload(fileName, replyAttachment, { cacheControl: '3600', upsert: false });
                    if (!uploadError) {
                        const { data: urlData } = supabase.storage.from('case-documents').getPublicUrl(fileName);
                        attachmentUrl = urlData.publicUrl;
                    }
                } catch (uploadErr) {
                    console.warn('File upload failed, continuing without attachment');
                }
            }
            if (attachmentUrl) messageData.attachment_url = attachmentUrl;

            // Insert message
            const { error } = await supabase.from('messages').insert([messageData]);
            if (error) throw error;

            // Send email notification to expert
            try {
                const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8001').trim();
                await fetch(`${apiUrl}/notify/message`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        recipient_email: expertMessage?.experts?.email || '',
                        recipient_name: expertMessage?.experts?.full_name || 'Expert',
                        sender_name: attorney.name,
                        case_title: selectedConversation.case_title,
                        is_anonymous: false
                    })
                });
            } catch (emailErr) {
                console.log('Email notification failed (non-blocking):', emailErr);
            }

            // Reset reply but keep conversation open so user sees thread update
            setReplyMessage('');
            setReplyAttachment(null);
            setAnonymousAttorneyReply(false);
            await loadAttorneyMessages(attorney.id);
        } catch (error: any) {
            setMessage(`Error sending reply: ${error.message}`);
        } finally {
            setSendingReply(false);
        }
    };

    // NEW: Direct Supabase search instead of backend API
    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!hasSubscription) {
            setShowSubscriptionModal(true);
            return;
        }
        setLoading(true);
        setMessage('');

        try {
            // Build the query for expert_directory
            let query = supabase
                .from('expert_directory')
                .select('*')
                .eq('is_verified', true);

            // Apply filters
            if (specialty) {
                query = query.ilike('specialty', `%${selectedSpecialty?.label || specialty}%`);
            }

            if (subSpecialty) {
                query = query.ilike('sub_specialty', `%${subSpecialty}%`);
            }

            if (minYears > 0) {
                query = query.gte('years_practicing', minYears);
            }

            if (maxRetainer > 0) {
                query = query.lte('retainer_fee', maxRetainer);
            }

            if (statesLicensed.length > 0) {
                query = query.overlaps('states_licensed', statesLicensed);
            }

            if (searchKeywords.trim()) {
                const keywords = searchKeywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k.length > 0);
                const orClauses = keywords.flatMap(kw => {
                    const term = `%${kw}%`;
                    return [
                        `specialty.ilike.${term}`,
                        `sub_specialty.ilike.${term}`,
                        `job_title.ilike.${term}`,
                        `case_experience.ilike.${term}`,
                        `extracted_keywords.ilike.${term}`,
                    ];
                });
                if (orClauses.length > 0) {
                    query = query.or(orClauses.join(','));
                }
            }

            // Execute the query
            const { data: expertData, error } = await query.limit(50); // Limit results

            if (error) {
                throw error;
            }

            // Transform data to match ExpertMatch interface
            const searchMatches: ExpertMatch[] = (expertData || []).map(expert => ({
                id: expert.id,
                specialty: expert.specialty,
                sub_specialty: expert.sub_specialty,
                years_practicing: expert.years_practicing,
                retainer_fee: expert.retainer_fee || 0,
                rate_review: expert.rate_review || 0,
                rate_dep: expert.rate_dep || 0,
                rate_trial: expert.rate_trial || 0,
                states_licensed: expert.states_licensed,
                currently_practicing: expert.currently_practicing,
                published_scholarly: expert.published_scholarly,
                is_verified: expert.is_verified,
                num_defense_depositions: expert.num_defense_depositions,
                num_plaintiff_depositions: expert.num_plaintiff_depositions,
                num_defense_trials: expert.num_defense_trials,
                num_plaintiff_trials: expert.num_plaintiff_trials,
                tier: expert.tier || 3,
                job_title: expert.job_title,
                expert_type: expert.expert_type,
                certifications: expert.certifications,
                case_experience: expert.case_experience,
                contact_status: expert.contact_status,
                // Don't include personal info - these will be revealed after expert responds
                full_name: undefined,
                email: undefined,
                phone_number: undefined,
                profile_picture_url: undefined
            }));

            setMatches(searchMatches);

            // Log search analytics
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                    await supabase.from('search_analytics').insert({
                        user_id: user.id,
                        filters_used: {
                            specialty: specialty || null,
                            sub_specialty: subSpecialty || null,
                            min_years: minYears || null,
                            max_retainer: maxRetainer || null,
                            states: statesLicensed.length > 0 ? statesLicensed : null,
                            keywords: searchKeywords || null
                        },
                        experts_viewed: [],
                        experts_contacted: [],
                        search_duration_seconds: 0
                    });
                }
            } catch (analyticsErr) {
                // Non-blocking
            }

            if (searchMatches.length === 0) {
                setMessage(`No matches found. Try adjusting your search criteria.`);
            } else {
                setMessage(''); // Clear any previous messages
            }

        } catch (error: any) {
            setMessage(`Error searching experts: ${error.message}`);
            console.error('Search error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleContact = (expert: ExpertMatch) => {
        setSelectedExpert(expert);
        setShowMessageModal(true);
        // Log profile view
        try {
            supabase.from('expert_interactions').insert({
                expert_id: expert.id,
                attorney_id: attorney?.id,
                interaction_type: 'viewed',
                case_specialty: specialty || null,
                case_type: null
            });
        } catch (e) {};
    };

    const renderAnonymousExpertAvatar = (expert: ExpertMatch, anonymousName: string) => {
        const tier = expert.tier || 3;
        const tierInfo = TIER_COLORS[tier as keyof typeof TIER_COLORS] || TIER_COLORS[3];
        return (
            <div className="flex-shrink-0">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border-4 ${tierInfo.border} shadow-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center`}>
                    <span className="text-xl sm:text-2xl font-bold text-gray-600">{anonymousName.split(' ')[1]}</span>
                </div>
            </div>
        );
    };

    const renderExpertWithTier = (expert: any, showPersonalInfo: boolean = false) => {
        const tierInfo = expert.tier ? TIER_COLORS[expert.tier as keyof typeof TIER_COLORS] : TIER_COLORS[3];
        if (!showPersonalInfo) {
            return (
                <div className="flex flex-col items-center">
                    <div className={`w-16 h-16 rounded-full overflow-hidden border-4 ${tierInfo.border} shadow-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center`}>
                        <span className="text-lg font-bold text-gray-600">EX</span>
                    </div>
                    <div className={`mt-1 px-2 py-1 rounded-full text-xs font-medium ${tierInfo.bg} ${tierInfo.text}`}>
                        {tierInfo.name}
                    </div>
                </div>
            );
        }
        return (
            <div className="flex flex-col items-center">
                <div className={`w-16 h-16 rounded-full overflow-hidden border-4 ${tierInfo.border} shadow-lg`}>
                    <img
                        src={expert.profile_picture_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(expert.full_name || '')}&background=0D8ABC&color=fff&size=200`}
                        alt="Expert"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className={`mt-1 px-2 py-1 rounded-full text-xs font-medium ${tierInfo.bg} ${tierInfo.text}`}>
                    {tierInfo.name}
                </div>
            </div>
        );
    };

    if (dashboardLoading || checkingSubscription) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    if (!attorney) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md">
                    <p className="text-gray-600 mb-4">No legal professional profile found. Please complete your profile.</p>
                    <button onClick={() => navigate('/attorney-intake')} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                        Complete Profile
                    </button>
                </div>
            </div>
        );
    }

    const conversations = messages.reduce((acc: any, msg: any) => {
        const otherUserId = msg.sender_type === 'attorney' ? msg.recipient_id : msg.sender_id;
        const key = `${otherUserId}-${(msg.case_title || 'untitled').toLowerCase().trim()}`;
        if (!acc[key]) {
            acc[key] = {
                expert_id: msg.expert_id,
                case_title: msg.case_title,
                messages: [],
                latest: msg.created_at,
                unread: 0,
                urgency: msg.urgency,
                case_type: msg.case_type,
                hospital_provider: msg.hospital_provider,
                incident_date: msg.incident_date,
                expert: msg.experts
            };
        }
        // Update expert info if this message has it (some anonymous messages won't)
        if (msg.experts && !acc[key].expert) {
            acc[key].expert = msg.experts;
        }
        if (msg.expert_id && !acc[key].expert_id) {
            acc[key].expert_id = msg.expert_id;
        }
        acc[key].messages.push(msg);
        if (new Date(msg.created_at) > new Date(acc[key].latest)) {
            acc[key].latest = msg.created_at;
        }
        if (!msg.read && msg.sender_type === 'expert') {
            acc[key].unread++;
        }
        return acc;
    }, {});

    const conversationList = Object.entries(conversations).map(([key, conv]: any) => ({
        key,
        ...conv,
        messages: conv.messages.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    })).sort((a: any, b: any) => new Date(b.latest).getTime() - new Date(a.latest).getTime());

    const unreadCount = messages.filter(m => !m.read && m.sender_type === 'expert').length;

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="bg-white rounded-lg shadow p-6 mb-8">
                    <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Welcome back, {attorney.name}</h1>
                    <p className="text-gray-600 mt-2">Search for expert witnesses and manage your case communications</p>
                    {unreadCount > 0 && (
                        <p className="mt-2 text-sm text-blue-600 font-medium">
                            You have {unreadCount} new message{unreadCount !== 1 ? 's' : ''}
                        </p>
                    )}
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-lg shadow mb-8">
                    <div className="border-b overflow-x-auto">
                        <nav className="flex -mb-px min-w-max">
                            <button
                                onClick={() => setActiveTab('search')}
                                className={`py-3 px-3 sm:py-4 sm:px-6 text-xs sm:text-sm font-medium border-b-2 transition ${
                                    activeTab === 'search'
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Search Experts
                            </button>
                            <button
                                onClick={() => setActiveTab('messages')}
                                className={`py-4 px-6 text-sm font-medium border-b-2 transition relative ${
                                    activeTab === 'messages'
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Messages ({conversationList.length})
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                        {unreadCount}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab('case-posts')}
                                className={`py-4 px-6 text-sm font-medium border-b-2 transition ${
                                    activeTab === 'case-posts'
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Post a Case
                            </button>
                        </nav>
                    </div>
                    {/* Expert Tier System Explanation */}

                    {/* Expert Tier System Explanation - Professional styling */}
                    <div className="mb-8 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center mb-4">
                                <svg className="w-6 h-6 mr-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                                <h3 className="text-xl font-bold text-gray-900">Expert Quality Tiers</h3>


                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                                {Object.entries(TIER_COLORS).sort((a,b)=>Number(a[0])-Number(b[0])).map(([tier, info]) => (
                                    <div key={tier} className={`p-4 rounded-lg border-2 ${info.border} bg-white hover:shadow-md transition-shadow`}>
                                        <div className={`font-bold ${info.text} text-sm mb-2`}>
                                            ⭐ Tier {tier}: {info.name}
                                        </div>
                                        <p className="text-xs text-gray-600 leading-relaxed">
                                            {info.description ?? "This tier reflects the expert's overall experience, qualifications, and vetting level."}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="p-6">
                        {activeTab === 'search' && (
                            <>
                                {/* Search Form */}
                                <div className="mb-8">
                                    <h2 className="text-xl font-semibold mb-6">Search for Experts</h2>
                                    <form onSubmit={handleSearch} className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Medical Specialty <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    required
                                                    value={specialty}
                                                    onChange={e => handleSpecialtyChange(e.target.value)}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                >
                                                    <option value="">Select a specialty</option>
                                                    {MEDICAL_SPECIALTIES.map(spec => (
                                                        <option key={spec.value} value={spec.value}>
                                                            {spec.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Sub-specialty</label>
                                                <select
                                                    value={subSpecialty}
                                                    onChange={e => setSubSpecialty(e.target.value)}
                                                    disabled={!specialty}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                                >
                                                    <option value="">Any sub-specialty</option>
                                                    {availableSubSpecialties.map(sub => (
                                                        <option key={sub} value={sub}>{sub}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Min. Years Experience</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={minYears}
                                                    onChange={e => setMinYears(+e.target.value)}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Max Retainer Fee ($)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={maxRetainer}
                                                    onChange={e => setMaxRetainer(+e.target.value)}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="No limit"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">States Licensed</label>
                                                <StateSelect
                                                    value={statesLicensed}
                                                    onChange={setStatesLicensed}
                                                    multiple={true}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            {/* Selected keywords */}
                                            {searchKeywords.trim() && (
                                                <div className="mb-3">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Active Keyword Filters:</label>
                                                    <div className="flex flex-wrap gap-2">
                                                        {searchKeywords.split(',').map(k => k.trim()).filter(k => k).map((kw, idx) => (
                                                            <span key={idx} className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-medium">
                                                                {kw}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const updated = searchKeywords.split(',').map(k => k.trim()).filter(k => k && k !== kw).join(', ');
                                                                        setSearchKeywords(updated);
                                                                    }}
                                                                    className="ml-2 text-blue-200 hover:text-white"
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        ))}
                                                        <button
                                                            type="button"
                                                            onClick={() => setSearchKeywords('')}
                                                            className="px-2 py-1 text-xs text-gray-500 hover:text-red-600"
                                                        >
                                                            Clear all
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Available keywords from admin-assigned tags */}
                                            {suggestedKeywords.length > 0 && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Keywords (Optional)</label>
                                                    <div className="flex flex-wrap gap-2">
                                                        {suggestedKeywords
                                                            .filter(kw => !searchKeywords.toLowerCase().includes(kw.toLowerCase()))
                                                            .map((keyword, idx) => (
                                                                <button
                                                                    key={idx}
                                                                    type="button"
                                                                    onClick={() => setSearchKeywords(prev => prev ? `${prev}, ${keyword}` : keyword)}
                                                                    className="px-3 py-1 bg-gray-100 text-gray-700 border border-gray-200 rounded-full text-sm hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition"
                                                                >
                                                                    + {keyword}
                                                                </button>
                                                            ))}
                                                    </div>
                                                    <p className="text-xs text-gray-400 mt-1">Click to filter by expert-specific keywords</p>
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className={`w-full py-3 px-4 rounded-lg text-white font-medium transition ${
                                                loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                                            }`}
                                        >
                                            {loading ? 'Searching...' : 'Search Experts'}
                                        </button>
                                    </form>

                                    {message && (
                                        <div className={`mt-4 p-4 rounded ${
                                            message.includes('No matches') ? 'bg-yellow-50 border border-yellow-200 text-yellow-700' : 'bg-red-50 border border-red-200 text-red-700'
                                        }`}>
                                            {message}
                                        </div>
                                    )}
                                </div>

                                {/* Results */}
                                {matches.length > 0 && (
                                    <div className="space-y-4">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Found {matches.length} Expert{matches.length !== 1 ? 's' : ''}</h3>
                                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                                            <div className="flex">
                                                <div className="flex-shrink-0">
                                                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm text-blue-700">
                                                        <strong>Privacy Notice:</strong> Expert identities and contact information are protected until they respond to your inquiry.
                                                        Personal details will be revealed only after the expert replies to your message. Tier colors indicate expert quality level.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {matches.map((expert, index) => {
                                            const totalCases = (expert.num_defense_depositions || 0) + (expert.num_plaintiff_depositions || 0) + (expert.num_defense_trials || 0) + (expert.num_plaintiff_trials || 0);
                                            const defenseTotal = (expert.num_defense_depositions || 0) + (expert.num_defense_trials || 0);
                                            const defensePercentage = totalCases > 0 ? Math.round((defenseTotal / totalCases) * 100) : 0;
                                            const getAnonymousName = (idx: number) => `Expert ${String.fromCharCode(65 + idx)}`;
                                            const tier = expert.tier || 3;
                                            const tierInfo = TIER_COLORS[tier as keyof typeof TIER_COLORS] || TIER_COLORS[3];

                                            return (
                                                <div key={expert.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                                                    <div className="p-4 sm:p-6">
                                                        <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-3 sm:space-y-0 sm:space-x-4">
                                                            {renderAnonymousExpertAvatar(expert, getAnonymousName(index))}
                                                            <div className="flex-1 w-full">
                                                                <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between text-center sm:text-left">
                                                                    <div>
                                                                        <div className="flex items-center space-x-3">
                                                                            <h4 className="text-lg font-semibold text-gray-900">{getAnonymousName(index)}</h4>
                                                                            <div className={`px-3 py-1 rounded-full text-xs font-medium ${tierInfo.bg} ${tierInfo.text} border ${tierInfo.border}`}>
                                                                                {tierInfo.name}
                                                                            </div>
                                                                        </div>
                                                                        <p className="text-sm text-gray-600">{expert.specialty} {expert.sub_specialty && `• ${expert.sub_specialty}`}</p>
                                                                        {expert.years_practicing && <p className="text-sm text-gray-500 mt-1">{expert.years_practicing} years of experience</p>}
                                                                        {expert.states_licensed && expert.states_licensed.length > 0 && (
                                                                            <p className="text-sm text-gray-500 mt-1">Licensed in: {expert.states_licensed.join(', ')}</p>
                                                                        )}
                                                                        {expert.job_title && (
                                                                            <p className="text-sm text-gray-500 mt-1">{expert.job_title}</p>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex flex-col items-end space-y-2">
                                                                        <span className={`px-3 py-1 text-xs rounded-full ${expert.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                                            {expert.is_verified ? '✓ Verified' : 'Pending'}
                                                                        </span>
                                                                        {expert.match_confidence && (
                                                                            <span className="px-3 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                                                                {expert.match_confidence}% Match
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                                                                    <h5 className="text-sm font-semibold text-gray-700 mb-2 text-center sm:text-left">Expert Witness Experience</h5>
                                                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                                                        <div className="text-center">
                                                                            <p className="text-2xl font-bold text-gray-900">{totalCases}</p>
                                                                            <p className="text-xs text-gray-500">Total Cases</p>
                                                                        </div>
                                                                        <div className="text-center">
                                                                            <p className="text-2xl font-bold text-blue-600">{defensePercentage}%</p>
                                                                            <p className="text-xs text-gray-500">Defense Side</p>
                                                                        </div>
                                                                        <div className="text-center">
                                                                            <p className="text-2xl font-bold text-gray-900">{(expert.num_defense_depositions || 0) + (expert.num_plaintiff_depositions || 0)}</p>
                                                                            <p className="text-xs text-gray-500">Depositions</p>
                                                                        </div>
                                                                        <div className="text-center">
                                                                            <p className="text-2xl font-bold text-gray-900">{(expert.num_defense_trials || 0) + (expert.num_plaintiff_trials || 0)}</p>
                                                                            <p className="text-xs text-gray-500">Trials</p>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {expert.case_experience && (
                                                                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                                                        <h5 className="text-sm font-semibold text-gray-700 mb-1">Case Experience</h5>
                                                                        <p className="text-sm text-gray-600">{expert.case_experience}</p>
                                                                    </div>
                                                                )}

                                                                {expert.certifications && expert.certifications.length > 0 && (
                                                                    <div className="mt-4">
                                                                        <h5 className="text-sm font-semibold text-gray-700 mb-2">Certifications</h5>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {expert.certifications.map((cert, idx) => (
                                                                                <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                                                                    {cert}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                                                                    <div>
                                                                        <p className="text-xs text-gray-500">Retainer Fee</p>
                                                                        <p className="text-sm font-semibold">${expert.retainer_fee || 'N/A'}</p>
                                                                    </div>
                                                                    {expert.rate_review && (
                                                                        <div>
                                                                            <p className="text-xs text-gray-500">Review Rate</p>
                                                                            <p className="text-sm font-semibold">${expert.rate_review}/hr</p>
                                                                        </div>
                                                                    )}
                                                                    {expert.rate_dep && (
                                                                        <div>
                                                                            <p className="text-xs text-gray-500">Deposition Rate</p>
                                                                            <p className="text-sm font-semibold">${expert.rate_dep}/hr</p>
                                                                        </div>
                                                                    )}
                                                                    {expert.rate_trial && (
                                                                        <div>
                                                                            <p className="text-xs text-gray-500">Trial Rate</p>
                                                                            <p className="text-sm font-semibold">${expert.rate_trial}/hr</p>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Additional Qualifications */}
                                                                <div className="mt-3 flex flex-wrap gap-2">
                                                                    {expert.currently_practicing && (
                                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                            Currently Practicing
                                                                        </span>
                                                                    )}
                                                                    {expert.published_scholarly && (
                                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                            Published Author
                                                                        </span>
                                                                    )}
                                                                    {expert.expert_type && (
                                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                            {expert.expert_type}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div className="mt-4">
                                                                    <button
                                                                        onClick={() => handleContact(expert)}
                                                                        disabled={!expert.is_verified}
                                                                        className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                                                                            expert.is_verified ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                        }`}
                                                                    >
                                                                        {expert.is_verified ? 'Contact Expert' : 'Verification Pending'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                        {activeTab === 'messages' && (
                            // Messages Tab
                            <div>
                                <h2 className="text-xl font-semibold mb-6">Your Messages</h2>
                                {conversationList.length === 0 ? (
                                    <div className="text-center py-12">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                        </svg>
                                        <p className="mt-2 text-gray-500">No messages yet</p>
                                        <p className="text-sm text-gray-400 mt-1">Contact experts to start conversations</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {conversationList.map((conv) => {
                                            const hasExpertResponded = expertResponses.has(conv.expert_id);
                                            return (
                                                <div
                                                    key={conv.key}
                                                    className="border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                                                    onClick={async () => {
                                                        setSelectedConversation(conv);
                                                        setCasePostDetails(null);
                                                        // Fetch case post details if available
                                                        if (conv.case_title) {
                                                            const { data: postData } = await supabase
                                                                .from('case_posts')
                                                                .select('*')
                                                                .eq('title', conv.case_title)
                                                                .eq('attorney_id', attorney.id)
                                                                .maybeSingle();
                                                            if (postData) setCasePostDetails(postData);
                                                        }
                                                        // Mark messages as read
                                                        const unreadIds = conv.messages.filter((m: any) => !m.read && m.sender_type === 'expert').map((m: any) => m.id);
                                                        if (unreadIds.length > 0) {
                                                            await supabase.from('messages').update({ read: true }).in('id', unreadIds);
                                                        }
                                                    }}
                                                >
                                                    <div className="p-4">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <div className="flex items-start space-x-4">
                                                                {conv.expert && !conv.messages.every((m: any) => m.sender_type === 'attorney' || m.is_anonymous) && renderExpertWithTier(conv.expert, hasExpertResponded)}
                                                                <div className="flex-1">
                                                                    <div className="flex items-center space-x-2">
                                                                        <h4 className="font-semibold text-gray-900">{conv.case_title || 'Untitled Case'}</h4>
                                                                        {conv.unread > 0 && (
                                                                            <span className="px-2 py-1 text-xs bg-blue-600 text-white rounded-full">{conv.unread} new</span>
                                                                        )}
                                                                        {!hasExpertResponded && (
                                                                            <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">Awaiting Response</span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-sm text-gray-600 mt-1">
                                                                        With: {hasExpertResponded ? `${conv.expert?.full_name || 'Expert'} (${conv.expert?.specialty})` : `Expert (${conv.expert?.specialty})`}
                                                                    </p>
                                                                    <p className="text-sm text-gray-700 mt-2 line-clamp-2">{conv.messages[0]?.message}</p>
                                                                </div>
                                                            </div>
                                                            <div className="text-right ml-4">
                                                                <p className="text-xs text-gray-500">{new Date(conv.latest).toLocaleDateString()}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {selectedConversation && (
                                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                                        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                                            <div className="p-6">
                                                <div className="flex justify-between items-start mb-4">
                                                    <div>
                                                        <h3 className="text-xl font-bold text-gray-900">{selectedConversation.case_title || 'Case Conversation'}</h3>
                                                        <p className="text-sm text-gray-600">
                                                            With: {(() => {
                                                            const expertRevealed = selectedConversation.messages.some((m: any) => m.sender_type === 'expert' && !m.is_anonymous && !m.anonymous_sender_id);
                                                            if (expertRevealed && selectedConversation.expert?.full_name) {
                                                                return `${selectedConversation.expert.full_name}${selectedConversation.expert.specialty ? ` (${selectedConversation.expert.specialty})` : ''}`;
                                                            }
                                                            return 'Anonymous Expert';
                                                        })()}
                                                        </p>
                                                    </div>
                                                    <button onClick={() => setSelectedConversation(null)} className="text-gray-400 hover:text-gray-600">
                                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>

                                                {/* Case Details */}
                                                {casePostDetails && (
                                                    <div className="bg-gray-50 rounded-lg p-4 mb-4 border">
                                                        <h4 className="font-semibold text-gray-900 mb-1">Case Post Details</h4>
                                                        <p className="text-sm text-gray-700 mb-2">{casePostDetails.description}</p>
                                                        <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                                                            {casePostDetails.specialty_needed && <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">{casePostDetails.specialty_needed}</span>}
                                                            {casePostDetails.case_type && casePostDetails.case_type !== 'not_specified' && <span className="bg-gray-200 px-2 py-1 rounded capitalize">{casePostDetails.case_type.replace('_', ' ')}</span>}
                                                            {casePostDetails.state_needed && <span className="bg-gray-200 px-2 py-1 rounded">{casePostDetails.state_needed}</span>}
                                                            {casePostDetails.budget_range && <span className="bg-green-100 text-green-800 px-2 py-1 rounded">{casePostDetails.budget_range}</span>}
                                                            {casePostDetails.urgency && casePostDetails.urgency !== 'standard' && <span className="bg-red-100 text-red-800 px-2 py-1 rounded capitalize">{casePostDetails.urgency}</span>}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Expert Documents - show after identity revealed */}
                                                {!selectedConversation.messages.every((m: any) => m.sender_type === 'attorney' || m.is_anonymous) && (selectedConversation.expert?.fee_schedule_url || selectedConversation.expert?.cv_url) && (
                                                    <div className="bg-blue-50 rounded-lg p-3 mb-4 border border-blue-200">
                                                        <p className="text-sm font-medium text-blue-900 mb-2">{selectedConversation.expert?.full_name}'s Documents</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {selectedConversation.expert?.fee_schedule_url && (
                                                                <a href={selectedConversation.expert.fee_schedule_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 inline-flex items-center">
                                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                                    Fee Schedule
                                                                </a>
                                                            )}
                                                            {selectedConversation.expert?.cv_url && (
                                                                <a href={selectedConversation.expert.cv_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 inline-flex items-center">
                                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                                                    CV / Resume
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
                                                    {selectedConversation.messages
                                                        .sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                                                        .map((msg: any) => (
                                                            <div key={msg.id} className={`p-4 rounded-lg ${msg.sender_type === 'attorney' ? 'bg-blue-50 ml-12' : 'bg-gray-50'}`}>
                                                                <div className="flex justify-between items-start mb-2">
                                                                    <p className="font-semibold text-gray-900">
                                                                        {msg.sender_type === 'attorney' ? 'You' : (() => {
                                                                            if (msg.is_anonymous) return 'Anonymous Expert';
                                                                            return selectedConversation.expert?.full_name || 'Expert';
                                                                        })()}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">{new Date(msg.created_at).toLocaleString()}</p>
                                                                </div>
                                                                <p className="text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                                                                {msg.attachment_url && (
                                                                    <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center mt-2 text-xs text-blue-600 hover:text-blue-800">
                                                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                                        </svg>
                                                                        View Attachment
                                                                    </a>
                                                                )}
                                                            </div>
                                                        ))}
                                                </div>

                                                <div className="border-t pt-4">
                                                    {/* Anonymous option - only show if attorney hasn't already revealed */}
                                                    {!selectedConversation.messages.some((m: any) => m.sender_type === 'attorney' && !m.is_anonymous) && (
                                                        <div className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                                                            <label className="flex items-start space-x-3 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={anonymousAttorneyReply}
                                                                    onChange={(e) => setAnonymousAttorneyReply(e.target.checked)}
                                                                    className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-2 border-gray-400 rounded"
                                                                />
                                                                <div className="flex-1">
                                                                    <span className="text-base font-bold text-gray-900">🔒 Keep my identity private</span>
                                                                    <p className="text-sm text-gray-700 mt-1">Your name, firm, email, and phone will NOT be shared with the expert.</p>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    )}
                                                    <textarea
                                                        value={replyMessage}
                                                        onChange={(e) => setReplyMessage(e.target.value)}
                                                        rows={4}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                        placeholder={anonymousAttorneyReply ? "Type your reply (sent anonymously)..." : "Type your reply..."}
                                                    />

                                                    {/* File attachment */}
                                                    <div className="mt-2 flex items-center gap-3">
                                                        <label className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 flex items-center">
                                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                            </svg>
                                                            Attach File
                                                            <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => setReplyAttachment(e.target.files?.[0] || null)} />
                                                        </label>
                                                        {replyAttachment && (
                                                            <span className="text-xs text-green-600 flex items-center">
                                                                ✓ {replyAttachment.name}
                                                                <button type="button" onClick={() => setReplyAttachment(null)} className="ml-1 text-red-500 hover:text-red-700">×</button>
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="mt-3 flex justify-end">
                                                        <button
                                                            onClick={sendReply}
                                                            disabled={sendingReply || !replyMessage.trim()}
                                                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                                                        >
                                                            {sendingReply ? 'Sending...' : anonymousAttorneyReply ? 'Send Anonymous Reply' : 'Send Reply'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {activeTab === 'case-posts' && (
                            <CasePosts />
                        )}
                    </div>
                </div>
            </div>

            {/* Message Modal */}
            {showMessageModal && selectedExpert && (
                <MessageModal
                    expert={selectedExpert}
                    attorney={attorney}
                    onClose={() => {
                        setShowMessageModal(false);
                        setSelectedExpert(null);
                        if (attorney) loadAttorneyMessages(attorney.id);
                    }}
                />
            )}

            {/* Subscription Modal */}
            {showSubscriptionModal && (
                <SubscriptionModal
                    onSuccess={() => {
                        setShowSubscriptionModal(false);
                        setHasSubscription(true);
                    }}
                    onCancel={() => setShowSubscriptionModal(false)}
                />
            )}
        </div>
    );
}